import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { type ScannedLine, ScanInput, addScan } from '../../components/scan-input';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { type Columns, DataTable } from '../../components/ui/data-table';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { api, errorMessage } from '../../lib/api';
import { can, useMe } from '../../lib/session';
import type { StockCountDetail, StockCountRow, TransferRow } from '../../lib/types';
import { formatDateTime } from '../../lib/utils';
import { LocationSelect, locationsQuery } from './stock';

/** Трансферы между складами и инвентаризация. */
export function StockDocumentsPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const [creatingTransfer, setCreatingTransfer] = useState(false);
  const [countId, setCountId] = useState<string>();
  const [countLocation, setCountLocation] = useState('');
  const locations = useQuery(locationsQuery);
  const transfers = useQuery({
    queryKey: ['warehouse', 'transfers'],
    queryFn: () => api<{ items: TransferRow[] }>('/warehouse/transfers'),
  });
  const counts = useQuery({
    queryKey: ['warehouse', 'counts'],
    queryFn: () => api<{ items: StockCountRow[] }>('/warehouse/counts'),
  });

  const act = useCallback(
    async (path: string, body?: unknown) => {
      setError(undefined);
      try {
        const result = await api<{ id?: string }>(path, { method: 'POST', body: body ?? {} });
        await queryClient.invalidateQueries({ queryKey: ['warehouse'] });
        return result;
      } catch (err) {
        setError(errorMessage(err));
        return undefined;
      }
    },
    [queryClient],
  );

  const transferColumns = useMemo<Columns<TransferRow>>(
    () => [
      { id: 'number', header: t('po.number'), accessorFn: (r) => r.number },
      { id: 'route', header: t('wh.route'), accessorFn: (r) => `${r.from} → ${r.to}` },
      {
        id: 'status',
        header: t('po.status'),
        accessorFn: (r) => r.status,
        cell: ({ row }) => (
          <Badge variant="secondary">{t(`wh.transferStatuses.${row.original.status}`)}</Badge>
        ),
      },
      {
        id: 'created',
        header: t('po.created'),
        accessorFn: (r) => r.createdAt,
        cell: ({ row }) => formatDateTime(row.original.createdAt, i18n.language),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="flex gap-1">
              {r.status === 'draft' && can(me, 'inventory.transfer') ? (
                <>
                  <Button size="sm" onClick={() => void act(`/warehouse/transfers/${r.id}/ship`)}>
                    {t('wh.shipTransfer')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void act(`/warehouse/transfers/${r.id}/cancel`)}
                  >
                    {t('common.cancel')}
                  </Button>
                </>
              ) : null}
              {r.status === 'in_transit' && can(me, 'inventory.receive') ? (
                <Button size="sm" onClick={() => void act(`/warehouse/transfers/${r.id}/receive`)}>
                  {t('wh.receiveAll')}
                </Button>
              ) : null}
            </span>
          );
        },
      },
    ],
    [t, i18n.language, me, act],
  );

  const countColumns = useMemo<Columns<StockCountRow>>(
    () => [
      {
        id: 'number',
        header: t('po.number'),
        accessorFn: (r) => r.number,
        cell: ({ row }) => (
          <button
            type="button"
            className="font-medium hover:underline"
            onClick={() => {
              setCountId(row.original.id);
            }}
          >
            {row.original.number}
          </button>
        ),
      },
      { id: 'location', header: t('wh.location'), accessorFn: (r) => r.locationCode },
      {
        id: 'status',
        header: t('po.status'),
        accessorFn: (r) => r.status,
        cell: ({ row }) => (
          <Badge variant="secondary">{t(`wh.countStatuses.${row.original.status}`)}</Badge>
        ),
      },
      {
        id: 'started',
        header: t('po.created'),
        accessorFn: (r) => r.startedAt ?? '',
        cell: ({ row }) => formatDateTime(row.original.startedAt, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  const firstLocation = countLocation || locations.data?.items[0]?.id || '';
  return (
    <>
      <PageHeader title={t('wh.documentsTitle')} description={t('wh.documentsHint')} />
      {error ? (
        <Alert variant="destructive" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('wh.transfers')}</h2>
        {can(me, 'inventory.transfer') ? (
          <Button
            onClick={() => {
              setCreatingTransfer(true);
            }}
          >
            {t('wh.newTransfer')}
          </Button>
        ) : null}
      </div>
      <DataTable
        columns={transferColumns}
        data={transfers.data?.items ?? []}
        loading={transfers.isPending}
        rowKey={(r) => r.id}
      />

      <div className="mb-3 mt-8 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{t('wh.counts')}</h2>
        {can(me, 'inventory.count') ? (
          <span className="flex gap-2">
            <LocationSelect value={firstLocation} onChange={setCountLocation} allowAll={false} />
            <Button
              onClick={() =>
                void act('/warehouse/counts', { locationId: firstLocation }).then((r) => {
                  if (r?.id) setCountId(r.id);
                })
              }
            >
              {t('wh.newCount')}
            </Button>
          </span>
        ) : null}
      </div>
      <DataTable
        columns={countColumns}
        data={counts.data?.items ?? []}
        loading={counts.isPending}
        rowKey={(r) => r.id}
      />
      {creatingTransfer ? (
        <TransferDialog
          onClose={() => {
            setCreatingTransfer(false);
          }}
        />
      ) : null}
      {countId ? (
        <CountDialog
          id={countId}
          onClose={() => {
            setCountId(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function TransferDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const locations = useQuery(locationsQuery);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [lines, setLines] = useState<ScannedLine[]>([]);
  const [error, setError] = useState<string>();
  const items = locations.data?.items ?? [];
  const fromId = from || items[0]?.id || '';
  const toId = to || items.find((l) => l.id !== fromId)?.id || '';

  const save = async () => {
    setError(undefined);
    try {
      await api('/warehouse/transfers', {
        method: 'POST',
        body: { fromLocationId: fromId, toLocationId: toId, lines },
      });
      await queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wh.newTransfer')}
      footer={
        <Button disabled={lines.length === 0 || fromId === toId} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3 text-sm">
        <div className="flex gap-2">
          <Field label={t('wh.from')}>
            {(id) => <LocationSelect id={id} value={fromId} onChange={setFrom} allowAll={false} />}
          </Field>
          <Field label={t('wh.to')}>
            {(id) => <LocationSelect id={id} value={toId} onChange={setTo} allowAll={false} />}
          </Field>
        </div>
        <ScanInput
          onScan={(code) => {
            setLines((l) => addScan(l, code));
          }}
        />
        <ul>
          {lines.map((l) => (
            <li key={l.code} className="font-mono">
              {l.code} × {l.qty}
            </li>
          ))}
        </ul>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}

function CountDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const count = useQuery({
    queryKey: ['warehouse', 'counts', id],
    queryFn: () => api<StockCountDetail>(`/warehouse/counts/${id}`),
  });
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const inProgress = count.data?.status === 'in_progress';

  const post = async (path: string, body?: unknown) => {
    setError(undefined);
    try {
      const result = await api<{ adjustments?: unknown[] }>(`/warehouse/counts/${id}/${path}`, {
        method: 'POST',
        body: body ?? {},
      });
      await queryClient.invalidateQueries({ queryKey: ['warehouse'] });
      return result;
    } catch (err) {
      setError(errorMessage(err));
      return undefined;
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${t('wh.count')} ${count.data?.number ?? ''}`}
      description={t('wh.countHint')}
      className="max-w-2xl"
      footer={
        inProgress ? (
          <Button
            onClick={() =>
              void post('complete').then((r) => {
                if (r) setDone(t('wh.countDone', { count: r.adjustments?.length ?? 0 }));
              })
            }
          >
            {t('wh.completeCount')}
          </Button>
        ) : null
      }
    >
      <div className="grid gap-4 text-sm">
        {inProgress ? (
          <ScanInput onScan={(code) => void post('lines', { code, qty: 1, mode: 'add' })} />
        ) : null}
        <table className="w-full">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">{t('wh.sku')}</th>
              <th>{t('wh.item')}</th>
              <th className="text-right">{t('wh.expected')}</th>
              <th className="text-right">{t('wh.counted')}</th>
            </tr>
          </thead>
          <tbody>
            {(count.data?.items ?? []).map((i) => (
              <tr key={i.id} className="border-t">
                <td className="py-1 font-mono">{i.sku}</td>
                <td>{i.name}</td>
                <td className="text-right">{i.expectedQty}</td>
                <td
                  className={
                    i.countedQty === null || i.countedQty === i.expectedQty
                      ? 'text-right'
                      : 'text-right text-amber-700'
                  }
                >
                  {i.countedQty ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
        {done ? <Alert variant="success">{done}</Alert> : null}
      </div>
    </Dialog>
  );
}
